import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, ArrowRight, Target, TrendingUp, Shield, Zap, BarChart3, FileCheck, Users, Settings, Clock, Activity, CheckCircle } from 'lucide-react';
import { testimonials, services, kpis, tools, faqs, industries, problemsSolved } from '../mock';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store in localStorage for mock
    const existingLeads = JSON.parse(localStorage.getItem('leads') || '[]');
    existingLeads.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('leads', JSON.stringify(existingLeads));
    
    toast({
      title: "Thank you for your interest!",
      description: "We'll be in touch within 24 hours.",
    });
    
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
  };

  const serviceIcons = {
    "Accounting & Compliance": FileCheck,
    "Reporting & Insights": BarChart3,
    "Due Diligence": Shield,
    "Operational Advisory": Settings
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#002029] via-[#00303D] to-[#004052] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#00607A] text-white border-none text-sm px-4 py-1.5">
              Your Strategic Partner in Growth
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Make Better Decisions with <span className="text-[#00607A]">Clear Numbers</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
              We bring structure, clarity, and growth readiness to businesses scaling globally.
            </p>
            
            <p className="text-lg mb-10 text-gray-400 max-w-2xl mx-auto">
              From clean books to stable processes and clear reporting - we build the setup that lets you move faster.
            </p>
            
            <p className="text-base mb-10 text-gray-400 max-w-2xl mx-auto">
              We partner with founders to design decision-ready frameworks in Finance, Operations, and Strategy. Numbers become clarity. Processes become scalable. Decisions become confident.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#00607A] hover:bg-[#005066] text-white px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105"
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#002029] px-8 py-6 text-lg transition-all duration-300"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              This is What We Help You Fix
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Common challenges that slow down growth and decision-making
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {problemsSolved.map((problem, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-[#00607A] transition-all duration-300 hover:shadow-md">
                <CheckCircle2 className="h-5 w-5 text-[#00607A] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{problem}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white scroll-mt-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive financial and operational solutions designed for growth
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const IconComponent = serviceIcons[service.title];
              const serviceIds = ['service-accounting', 'service-reporting', 'service-diligence', 'service-advisory'];
              return (
                <Card id={serviceIds[index]} key={service.id} className="border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 hover:shadow-xl group scroll-mt-24">
                  <CardHeader>
                    <div className="mb-4 p-3 bg-[#002029] w-fit rounded-lg group-hover:bg-[#00607A] transition-colors duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-[#002029] mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base text-gray-600">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-[#00607A] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 bg-[#002029] text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Trusted by businesses across diverse sectors
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {industries.map((industry, index) => (
              <Badge key={index} className="bg-[#004052] hover:bg-[#00607A] text-white border-none px-6 py-3 text-base transition-colors duration-300">
                {industry.name}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* KPIs Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Results That Matter
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Measurable improvements our clients experience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {kpis.map((kpi, index) => {
              const iconMap = {
                'trendingUp': TrendingUp,
                'target': Target,
                'clock': Clock,
                'checkCircle': CheckCircle,
                'zap': Zap,
                'activity': Activity
              };
              const IconComponent = iconMap[kpi.icon] || TrendingUp;
              
              return (
                <div key={index} className="bg-white p-8 rounded-xl border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 hover:shadow-xl text-center group">
                  <div className="mb-4 inline-block p-3 bg-[#002029] rounded-lg group-hover:bg-[#00607A] transition-colors duration-300">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-[#00607A] mb-2">{kpi.value}</div>
                  <div className="text-lg font-semibold text-[#002029] mb-1">{kpi.label}</div>
                  <div className="text-sm text-gray-500">{kpi.period}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Client Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real results from real businesses
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-200 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <CardTitle className="text-xl text-[#002029] mb-1">{testimonial.company}</CardTitle>
                      <Badge className="bg-[#00607A] text-white border-none">{testimonial.industry}</Badge>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-[#004052] mb-4">{testimonial.revenue}</div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic mb-6 leading-relaxed">"{testimonial.quote}"</p>
                  <div className="space-y-2">
                    <div className="font-semibold text-[#002029] text-sm mb-2">Key Results:</div>
                    {testimonial.results.map((result, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Target className="h-4 w-4 text-[#00607A] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{result}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">- {testimonial.author}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Tools We Work With
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Leveraging industry-leading platforms for optimal results
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
            {tools.map((tool, index) => (
              <div key={index} className="bg-white px-6 py-4 rounded-lg border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 hover:shadow-md">
                <div className="font-semibold text-[#002029]">{tool.name}</div>
                <div className="text-sm text-gray-500">{tool.category}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact Form Section */}
      <section id="contact-form" className="py-20 bg-gradient-to-br from-[#002029] via-[#00303D] to-[#004052] text-white scroll-mt-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Financial Operations?
              </h2>
              <p className="text-xl text-gray-300">
                Let's discuss how we can help you achieve clarity, structure, and growth readiness.
              </p>
            </div>
            
            <Card className="border-none shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#002029]">Get in Touch</CardTitle>
                <CardDescription>Fill out the form below and we'll respond within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your full name"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        placeholder="Your company name"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 000-0000"
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How can we help? *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Tell us about your business and what challenges you're facing..."
                      rows={5}
                      className="w-full"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#00607A] hover:bg-[#005066] text-white py-6 text-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Send Message
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about working with us
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-[#002029] hover:text-[#00607A] py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Why Choose Venura?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We bridge technical finance and execution
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="mb-6 inline-block p-4 bg-[#002029] rounded-xl">
                <Zap className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#002029] mb-3">Structure & Clarity</h3>
              <p className="text-gray-600">
                We design systems that leaders trust, bringing order to financial chaos and enabling confident decision-making.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mb-6 inline-block p-4 bg-[#002029] rounded-xl">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#002029] mb-3">Built for Growth</h3>
              <p className="text-gray-600">
                Whether you're fundraising, scaling, or preparing for exit, we ensure your financials tell the right story.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mb-6 inline-block p-4 bg-[#002029] rounded-xl">
                <Target className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#002029] mb-3">Global Support</h3>
              <p className="text-gray-600">
                We support businesses scaling globally with multi-currency reporting and international operational structures.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;